import type { RoomState } from '@tv-casino/shared';
import { formatSignedChips } from '../lib/format.js';

/**
 * Who won what on the last settle, as the TV celebrates it. Every game
 * reduces to the same two numbers per seat — everything staked, everything
 * returned — so one tiering rule decides how loud the felt gets for a blackjack
 * table, a Hold'em pot, a straight-up roulette hit or a hard eight.
 *
 * Pure data: no React, no timers. The key changes exactly when a new win lands,
 * so the renderer can remount its effects on it and nothing replays on the
 * next broadcast.
 */

/** How big a win reads: a plain win, a big one, or the one the room stands up for. */
export type WinTier = 'win' | 'big' | 'jackpot';

export interface SeatWin {
  seatIndex: number;
  name: string;
  /** Profit on the settle: returned minus staked. Always positive. */
  net: number;
  stake: number;
  /** "+1,750" */
  label: string;
  tier: WinTier;
  /** Changes whenever a new win lands on this seat. */
  key: string;
}

/** The headline the whole felt stops for: the biggest win of the settle. */
export interface WinHeadline {
  title: string;
  name: string;
  label: string;
  tier: Exclude<WinTier, 'win'>;
  /** How many other seats also won big on the same settle. */
  others: number;
  key: string;
}

/** Profit to stake at or above which a win is a jackpot: 10:1 and up. */
export const JACKPOT_RATIO = 10;
/** Profit to stake at or above which a win is big: 2:1 and up. */
export const BIG_RATIO = 2;

export function winTier(net: number, stake: number, natural = false): WinTier {
  const ratio = stake > 0 ? net / stake : 0;
  if (ratio >= JACKPOT_RATIO) return 'jackpot';
  if (ratio >= BIG_RATIO || natural) return 'big';
  return 'win';
}

const sum = (values: number[]): number => values.reduce((n, v) => n + v, 0);

interface Settle {
  seatIndex: number;
  stake: number;
  payout: number;
  natural?: boolean;
}

/** Each seat's staked and returned chips on the settle on screen, or [] mid-hand. */
function settles(state: RoomState): { settles: Settle[]; tag: string } {
  const round = state.round;
  switch (state.gameType) {
    case 'blackjack': {
      if (state.phase !== 'payout') return { settles: [], tag: '' };
      return {
        tag: `bj-${round}`,
        settles: state.seats
          .filter((s) => s.hands.some((h) => h.result !== null))
          .map((s) => ({
            seatIndex: s.index,
            stake:
              sum(s.hands.map((h) => h.bet)) + sum(s.sideBets.map((b) => b.stake)) + s.insuranceBet,
            payout:
              sum(s.hands.map((h) => h.payout ?? 0)) +
              sum(s.sideBets.map((b) => b.payout ?? 0)) +
              (s.insurancePayout ?? 0),
            natural: s.hands.some((h) => h.result === 'blackjack'),
          })),
      };
    }
    case 'holdem': {
      const h = state.holdem;
      if (!h?.results) return { settles: [], tag: '' };
      return {
        tag: `he-${round}`,
        settles: h.seats.map((s) => ({
          seatIndex: s.index,
          stake: s.totalCommitted,
          payout: s.won ?? 0,
        })),
      };
    }
    case 'baccarat': {
      const b = state.baccarat;
      if (!b?.results) return { settles: [], tag: '' };
      return {
        tag: `bac-${round}`,
        settles: b.seats.map((s) => {
          const main = b.results!.find((r) => r.seatIndex === s.index);
          return {
            seatIndex: s.index,
            stake: (main?.stake ?? 0) + sum(s.sideBets.map((x) => x.stake)),
            payout: (main?.payout ?? 0) + sum(s.sideBets.map((x) => x.payout ?? 0)),
          };
        }),
      };
    }
    case 'threecard': {
      const t = state.threecard;
      if (!t?.results) return { settles: [], tag: '' };
      return {
        tag: `tc-${t.handNumber}`,
        settles: t.results.map((r) => ({
          seatIndex: r.seatIndex,
          stake:
            r.ante +
            r.play +
            (t.seats.find((s) => s.index === r.seatIndex)?.pairPlus?.stake ?? 0),
          payout: r.payout,
        })),
      };
    }
    case 'roulette': {
      const r = state.roulette;
      if (!r?.results) return { settles: [], tag: '' };
      return {
        tag: `spin-${r.spinNumber}`,
        settles: r.results.map((x) => ({ seatIndex: x.seatIndex, stake: x.stake, payout: x.payout })),
      };
    }
    case 'craps': {
      // Craps pays roll by roll, so every decision the dice make is its own settle.
      const c = state.craps;
      if (!c || c.lastSettlements.length === 0) return { settles: [], tag: '' };
      const bySeat = new Map<number, Settle>();
      for (const s of c.lastSettlements) {
        const seat = bySeat.get(s.seatIndex) ?? { seatIndex: s.seatIndex, stake: 0, payout: 0 };
        // A bet that stood this roll neither paid nor lost; it is not part of the settle.
        if (s.resolution !== 'stand') {
          seat.stake += s.amount;
          seat.payout += s.payout;
        }
        bySeat.set(s.seatIndex, seat);
      }
      return { tag: `roll-${round}-${c.rollNumber}`, settles: [...bySeat.values()] };
    }
    case 'keno': {
      const k = state.keno;
      if (!k?.results) return { settles: [], tag: '' };
      return {
        tag: `keno-${k.drawNumber}`,
        settles: k.results.map((x) => ({ seatIndex: x.seatIndex, stake: x.stake, payout: x.payout })),
      };
    }
  }
}

/** Every seat that came out ahead on the settle on screen, in seat order. */
export function seatWins(state: RoomState | null): SeatWin[] {
  if (!state) return [];
  const { settles: rows, tag } = settles(state);
  return rows
    .map((row): SeatWin | null => {
      const net = row.payout - row.stake;
      if (net <= 0) return null;
      return {
        seatIndex: row.seatIndex,
        name: state.seats[row.seatIndex]?.name ?? `Seat ${row.seatIndex + 1}`,
        net,
        stake: row.stake,
        label: formatSignedChips(net),
        tier: winTier(net, row.stake, row.natural),
        key: `${tag}-${row.seatIndex}`,
      };
    })
    .filter((w): w is SeatWin => w !== null)
    .sort((a, b) => a.seatIndex - b.seatIndex);
}

/** The settle's biggest win, when it is big enough to stop the table for. */
export function winHeadline(wins: SeatWin[]): WinHeadline | null {
  const big = wins.filter((w) => w.tier !== 'win');
  if (big.length === 0) return null;
  const rank = (w: SeatWin) => (w.tier === 'jackpot' ? 1 : 0);
  const top = [...big].sort((a, b) => rank(b) - rank(a) || b.net - a.net)[0]!;
  return {
    title: top.tier === 'jackpot' ? 'Jackpot' : 'Big win',
    name: top.name,
    label: top.label,
    tier: top.tier as WinHeadline['tier'],
    others: big.length - 1,
    key: top.key,
  };
}
